import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAssistant } from '../../contexts/AssistantContext';

const MIN_WIDTH = 320;
const MAX_WIDTH = 800;
const MIN_HEIGHT = 400;
const DEFAULT_WIDTH = 380;
const DEFAULT_HEIGHT = 560;
const WIDTH_STORAGE_KEY = 'beacon-width';
const HEIGHT_STORAGE_KEY = 'beacon-height';

const suggestedPrompts = [
  'Show me all open P1 incidents',
  'How do I configure deduplication?',
  'What incidents are assigned to me?',
  'Show recent pipeline errors',
];

const clampWidth = (value: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value));
const clampHeight = (value: number) => {
  if (typeof window === 'undefined') {
    return Math.max(MIN_HEIGHT, value);
  }

  return Math.min(window.innerHeight * 0.9, Math.max(MIN_HEIGHT, value));
};

const getStoredSize = (key: string, fallback: number, clamp: (value: number) => number) => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  const storedValue = window.localStorage.getItem(key);
  const parsedValue = Number.parseInt(storedValue ?? '', 10);
  if (Number.isNaN(parsedValue)) {
    return clamp(fallback);
  }

  return clamp(parsedValue);
};

const formatMessageTime = (timestamp: string) => {
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

function FeedbackButtons({ messageIndex, feedback, onFeedback }: {
  messageIndex: number;
  feedback?: 'positive' | 'negative' | null;
  onFeedback: (index: number, isPositive: boolean, comment?: string) => void;
}) {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [comment, setComment] = useState('');

  if (feedback) {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
        {feedback === 'positive' ? (
          <span aria-label="Positive feedback submitted">👍 Thanks for the feedback!</span>
        ) : (
          <span aria-label="Negative feedback submitted">👎 Thanks, we&apos;ll improve.</span>
        )}
      </div>
    );
  }

  if (showCommentInput) {
    return (
      <div className="mt-1.5 flex flex-col gap-1">
        <input
          type="text"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="What was wrong? (optional)"
          className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onFeedback(messageIndex, false, comment || undefined);
              setShowCommentInput(false);
            }
          }}
        />
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => {
              onFeedback(messageIndex, false, comment || undefined);
              setShowCommentInput(false);
            }}
            className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
          >
            Submit
          </button>
          <button
            type="button"
            onClick={() => setShowCommentInput(false)}
            className="rounded px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
      <button
        type="button"
        onClick={() => onFeedback(messageIndex, true)}
        className="rounded p-0.5 text-gray-400 hover:bg-green-50 hover:text-green-600 dark:text-gray-500 dark:hover:bg-green-900/20 dark:hover:text-green-400"
        aria-label="Good response"
        title="Good response"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z M4 21h1a1 1 0 001-1v-9a1 1 0 00-1-1H4" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => setShowCommentInput(true)}
        className="rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:text-gray-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
        aria-label="Bad response"
        title="Bad response — provide feedback"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 15V19a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z M20 2h-1a1 1 0 00-1 1v9a1 1 0 001 1h1" />
        </svg>
      </button>
    </div>
  );
}

export function AssistantPane() {
  const navigate = useNavigate();
  const {
    isOpen,
    isLoading,
    messages,
    closePane,
    sendMessage,
    startNewConversation,
    submitFeedback,
  } = useAssistant();
  const [input, setInput] = useState('');
  const [width, setWidth] = useState(() => getStoredSize(WIDTH_STORAGE_KEY, DEFAULT_WIDTH, clampWidth));
  const [height, setHeight] = useState(() => getStoredSize(HEIGHT_STORAGE_KEY, DEFAULT_HEIGHT, clampHeight));
  const [isMaximized, setIsMaximized] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const resizeEdgeRef = useRef<'left' | 'top' | null>(null);
  const resizeStartRef = useRef({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isMaximized) {
          setIsMaximized(false);
          return;
        }

        closePane();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closePane, isMaximized, isOpen]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(Math.round(width)));
    window.localStorage.setItem(HEIGHT_STORAGE_KEY, String(Math.round(height)));
  }, [height, width]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleWindowResize = () => {
      setWidth((currentWidth) => clampWidth(currentWidth));
      setHeight((currentHeight) => clampHeight(currentHeight));
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!resizeEdgeRef.current || isMaximized) {
        return;
      }

      if (resizeEdgeRef.current === 'left') {
        const delta = resizeStartRef.current.x - event.clientX;
        const nextWidth = clampWidth(resizeStartRef.current.width + delta);
        setWidth(nextWidth);
        return;
      }

      const delta = resizeStartRef.current.y - event.clientY;
      const nextHeight = clampHeight(resizeStartRef.current.height + delta);
      setHeight(nextHeight);
    };

    const stopResizing = () => {
      if (!resizeEdgeRef.current) {
        return;
      }

      resizeEdgeRef.current = null;
      setIsResizing(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isMaximized]);

  const isConversationEmpty = useMemo(() => (messages ?? []).length === 0, [messages]);

  const startResize = (edge: 'left' | 'top', event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMaximized) {
      return;
    }

    event.preventDefault();
    resizeEdgeRef.current = edge;
    resizeStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      width,
      height,
    };

    setIsResizing(true);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = edge === 'left' ? 'ew-resize' : 'ns-resize';
  };

  const applyPreset = (nextWidth: number, nextHeight: number) => {
    setIsMaximized(false);
    setWidth(clampWidth(nextWidth));
    setHeight(clampHeight(nextHeight));
  };

  const handleSend = async () => {
    const message = input.trim();
    if (!message || isLoading) {
      return;
    }

    setInput('');
    await sendMessage(message);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <aside
      className={`fixed z-50 flex flex-col border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 ${
        isMaximized
          ? 'top-0 left-0 right-0 bottom-0 h-screen w-screen rounded-none shadow-none'
          : 'bottom-4 right-4 rounded-xl shadow-2xl'
      } ${isResizing ? 'transition-none' : 'transition-[width,height,top,left,right,bottom,border-radius] duration-200 ease-out'}`}
      style={isMaximized ? undefined : { width, height }}
      aria-label="Beacon assistant panel"
    >
      {!isMaximized && (
        <>
          <button
            type="button"
            className="absolute inset-y-0 left-0 z-10 w-2 cursor-ew-resize rounded-l-xl border-none bg-transparent p-0 transition-colors hover:bg-violet-500/10 active:bg-violet-500/20"
            onMouseDown={(event) => startResize('left', event)}
            aria-label="Resize Beacon width"
          />
          <button
            type="button"
            className="absolute inset-x-0 top-0 z-10 h-2 cursor-ns-resize rounded-t-xl border-none bg-transparent p-0 transition-colors hover:bg-violet-500/10 active:bg-violet-500/20"
            onMouseDown={(event) => startResize('top', event)}
            aria-label="Resize Beacon height"
          />
        </>
      )}

      {/* Header */}
      <div className={`flex items-center justify-between border-b border-gray-200 px-4 py-2.5 dark:border-gray-700 ${isMaximized ? '' : 'rounded-t-xl'}`}>
        <div className="flex items-center gap-2">
          <span aria-hidden="true">✨</span>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Beacon</h2>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => void startNewConversation()}
            className="rounded-md bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100 dark:bg-violet-900/30 dark:text-violet-200 dark:hover:bg-violet-900/50"
          >
            New
          </button>
          <button
            type="button"
            onClick={() => applyPreset(380, 560)}
            className="rounded-md px-1.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          >
            Small
          </button>
          <button
            type="button"
            onClick={() => applyPreset(500, 700)}
            className="rounded-md px-1.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          >
            Medium
          </button>
          <button
            type="button"
            onClick={() => applyPreset(700, 900)}
            className="rounded-md px-1.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          >
            Large
          </button>
          <button
            type="button"
            onClick={() => setIsMaximized((current) => !current)}
            className="rounded-md px-1.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
            aria-label={isMaximized ? 'Restore Beacon' : 'Fullscreen Beacon'}
            title={isMaximized ? 'Restore' : 'Fullscreen'}
          >
            {isMaximized ? 'Restore' : 'Fullscreen'}
          </button>
          <button
            type="button"
            onClick={closePane}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            aria-label="Close Beacon"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {isConversationEmpty && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">Try asking Beacon:</p>
            {suggestedPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => setInput(prompt)}
                className="block w-full rounded-lg border border-violet-100 bg-violet-50 px-3 py-2 text-left text-xs text-violet-800 hover:bg-violet-100 dark:border-violet-900/40 dark:bg-violet-900/20 dark:text-violet-200 dark:hover:bg-violet-900/30"
              >
                {prompt}
              </button>
            ))}
          </div>
        )}

        {messages.map((message, index) => (
          <div
            key={`${message.timestamp}-${index}`}
            className={`group max-w-[90%] rounded-xl px-3 py-2 text-sm ${
              message.role === 'user'
                ? 'ml-auto bg-primary-600 text-white'
                : 'mr-auto bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
            }`}
          >
            {message.role === 'assistant' ? (
              <>
                <ReactMarkdown
                  components={{
                    a: ({ href, children }) => {
                      if (href?.startsWith('/')) {
                        return (
                          <button
                            type="button"
                            className="text-primary-600 underline dark:text-primary-300"
                            onClick={() => navigate(href)}
                          >
                            {children}
                          </button>
                        );
                      }

                      return (
                        <a href={href} target="_blank" rel="noreferrer" className="text-primary-600 underline dark:text-primary-300">
                          {children}
                        </a>
                      );
                    },
                  }}
                >
                  {message.content ?? ""}
                </ReactMarkdown>
                <FeedbackButtons
                  messageIndex={index}
                  feedback={message.feedback}
                  onFeedback={(idx, isPositive, comment) => void submitFeedback(idx, isPositive, comment)}
                />
                <div className="mt-1 flex items-center justify-end text-[11px] text-gray-500 dark:text-gray-400">
                  <span>{formatMessageTime(message.timestamp)}</span>
                  {message.creditsCharged != null && message.creditsCharged > 0 && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      {message.creditsCharged} credit{message.creditsCharged !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <span>{message.content}</span>
                <div className="mt-1 text-right text-[11px] text-white/80">
                  {formatMessageTime(message.timestamp)}
                </div>
              </>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="mr-auto rounded-xl bg-gray-100 px-3 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
            <span className="inline-flex gap-1">
              <span className="h-2 w-2 animate-pulse rounded-full bg-current" />
              <span className="h-2 w-2 animate-pulse rounded-full bg-current [animation-delay:120ms]" />
              <span className="h-2 w-2 animate-pulse rounded-full bg-current [animation-delay:240ms]" />
            </span>
          </div>
        )}
      </div>

      {/* Input */}
      <div className={`border-t border-gray-200 p-3 dark:border-gray-700 ${isMaximized ? '' : 'rounded-b-xl'}`}>
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void handleSend();
              }
            }}
            rows={2}
            placeholder="Ask Beacon..."
            className="min-h-[44px] flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none ring-primary-500 placeholder:text-gray-400 focus:ring-2 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
          <button
            type="button"
            disabled={isLoading || input.trim().length === 0}
            onClick={() => void handleSend()}
            className="rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </div>
    </aside>
  );
}
